import { useMemo } from 'react';
import type { RequiredDocumentSection } from '../models/rental-application';
import { useRentalApplication } from './useRentalApplication';
import { useRetrieveActiveDocuments } from './useRetrieveActiveDocuments';
import { isIncomeDocumentType } from '../constants/incomeDocs';

function isIncomeDocumentSection(section: RequiredDocumentSection) {
  return !!section.documentType && isIncomeDocumentType(section.documentType);
}

export function isRequiredSection(section: RequiredDocumentSection) {
  return isIncomeDocumentSection(section) && section.requestedBy !== null && section.requestedBy !== undefined;
}

export function getSectionDocumentIds(sections: Array<RequiredDocumentSection>) {
  const ids = sections
    .flatMap((section) => section.documents ?? [])
    .map((document) => document.id)
    .filter((id): id is string => !!id);

  return [...new Set(ids)].sort();
}

/**
 * Reads the landlord's required document sections off the application answers and fills in file
 * name, preview url and redaction state for each attached document. Orphan sections are sections
 * whose document type the landlord no longer requires; they are returned for counting only
 */
export function useRequiredDocumentSections(applicationId: string | undefined) {
  const { application, isPending, isError } = useRentalApplication(applicationId);
  const sections = application?.answers?.requiredDocuments ?? undefined;

  const documentIdStubs = useMemo(() => getSectionDocumentIds(sections ?? []).map((id) => ({ id })), [sections]);
  const { documents: activeDocuments, isPending: isDocumentMetadataPending } =
    useRetrieveActiveDocuments(documentIdStubs);

  const hydratedSections = useMemo(
    () =>
      sections?.map((section) => ({
        ...section,
        // A frozen application can hold an id the documents api cannot resolve, so keep the stub
        documents: (section.documents ?? []).map(
          (document) => activeDocuments?.find((active) => active.id === document.id) ?? document,
        ),
      })),
    [sections, activeDocuments],
  );

  return {
    requiredSections: hydratedSections?.filter(isRequiredSection),
    orphanSections: hydratedSections?.filter(
      (section) => isIncomeDocumentSection(section) && !isRequiredSection(section),
    ),
    isLoading: !!applicationId && isPending,
    isError,
    isDocumentMetadataPending,
  };
}
